import React, { createContext, useContext, useState, ReactNode } from 'react';

interface Achievement {
  _id: string;
  name: string;
  description: string;
  icon: string;
  tier: 'bronze' | 'silver' | 'gold' | 'platinum';
  imageUrl?: string;
}

interface AchievementContextType {
  showAchievementPopup: (achievements: Achievement[]) => void;
  hideAchievementPopup: () => void;
  currentAchievements: Achievement[];
  isPopupVisible: boolean;
}

const AchievementContext = createContext<AchievementContextType | undefined>(undefined);

export const useAchievement = () => {
  const context = useContext(AchievementContext);
  if (context === undefined) {
    throw new Error('useAchievement must be used within an AchievementProvider');
  }
  return context;
};

interface AchievementProviderProps {
  children: ReactNode;
}

export const AchievementProvider: React.FC<AchievementProviderProps> = ({ children }) => {
  const [currentAchievements, setCurrentAchievements] = useState<Achievement[]>([]);
  const [isPopupVisible, setIsPopupVisible] = useState(false);

  const showAchievementPopup = (achievements: Achievement[]) => {
    setCurrentAchievements(achievements);
    setIsPopupVisible(true);
  };

  const hideAchievementPopup = () => {
    setIsPopupVisible(false);
    setCurrentAchievements([]);
  };

  const value: AchievementContextType = {
    showAchievementPopup,
    hideAchievementPopup,
    currentAchievements,
    isPopupVisible,
  };

  return (
    <AchievementContext.Provider value={value}>
      {children}
    </AchievementContext.Provider>
  );
}; 